import { Skeleton } from '@mui/material';
import { isUndefined } from 'lodash';
import ScrollContainer from 'react-indiana-drag-scroll';

import { Text } from '@/design-system';
import { useInfluencerCreatorsQuery } from '../../../../api/recipe/hooks/useInfluencerCreatorsQuery';
import SpaceY from '../../../../components/SpaceY';
import UImage from '../../../../components/UImage';
import { useAlertModal } from '../../../../utils/hook/hook';
import { useOpenRecipeModal } from '../../../../utils/hook/mypage/hook';
import { useMySubscriptionsQuery } from '../../../../utils/hook/react-query/useMySubscriptionsQuery';
import useMeQuery from '../../../../utils/hook/react-query/user/useMeQuery';
import { useHistoryMethod } from '../../../../utils/hook/useHistoryMethod';
import RecipePaperPolicy from '../../../../utils/policy/RecipePaperPolicy';
import { CreatorPageCategoryQuery } from '../../../MyPages/CreatorPage';
import RecipeMainBanner from '../components/RecipeMainBanner';

/** 로딩 중 표시할 스켈레톤 크리에이터 수 */
const CREATOR_SKELETON_COUNT = 10;

const creatorIcon = `https://uglyus.s3.ap-northeast-2.amazonaws.com/assets/recipe/creator_gray800_36x36.svg`;
const bookmarkIcon = `https://uglyus.s3.ap-northeast-2.amazonaws.com/assets/recipe/bookmark_gray800_36x36.svg`;
const recipePaperIcon = `https://uglyus.s3.ap-northeast-2.amazonaws.com/assets/recipe/recipepaper_gray800_36x36.svg`;

const RecipeMainCertifiedCreators = () => {
  const { me } = useMeQuery();
  const history = useHistoryMethod();
  const { openAlertModal } = useAlertModal();
  const { influencerCreators, isPending } = useInfluencerCreatorsQuery();

  /**
   * 로그인 사용자의 경우, DELIVERING_PREPARE 이후인 오더중 가장 최신의 오더를 가져온다.
   * - 이유: 구독이 여러개인 경우 오더를 특정할 수 없기 때문에
   */
  const { data } = useMySubscriptionsQuery();

  const latestHasRecipePaperOrder =
    RecipePaperPolicy.getLatestHasRecipePaperOrder(data ?? []);

  const openRecipeModal = useOpenRecipeModal(latestHasRecipePaperOrder);

  const handleMoveToMyDiary = () => {
    // 로그인이 안 된 경우
    if (isUndefined(me)) {
      return openAlertModal({
        content: '로그인이 필요한 페이지입니다.',
        buttonText: '로그인',
        hasCancelButton: true,
        onComplete: () =>
          history.pushToLogin({ redirectURI: '/recipes/bookmark' }),
      });
    }
    // 크리에이터 프로필이 없는 경우
    if (!me?.hasRecipeCreatorProfile) {
      return history.push('/recipes/bookmark');
    }
    history.push('/mypages/creator');
  };

  return (
    <div>
      <p className="px-16px text-22px font-bold">어글리어스 밥풀루언서</p>
      <ScrollContainer className="flex justify-evenly mt-[24px] gap-[16px]">
        <div className="h-[1px] w-[16px] flex-shrink-0" />
        {isPending
          ? Array.from({ length: CREATOR_SKELETON_COUNT }).map((_, idx) => (
              <div
                key={idx}
                className="flex w-[80px] h-[110px] flex-shrink-0 flex-col items-center justify-center gap-[8px] sm:w-[90px] sm:h-[120px]"
              >
                <Skeleton variant="circular" width={72} height={72} />
                <Skeleton variant="text" width={60} height={20} />
              </div>
            ))
          : influencerCreators.map((creator) => (
              <button
                key={creator.id}
                onClick={() =>
                  history.push(
                    `/recipes/creator/${creator.name}?category=${CreatorPageCategoryQuery.diary}`,
                  )
                }
                className="flex w-[80px] h-[110px] flex-shrink-0 flex-col justify-center items-center gap-[8px] sm:w-[90px] sm:h-[120px]"
                type="button"
              >
                <div className="relative">
                  <div className="h-[72px] w-[72px] overflow-hidden rounded-full ring-1 ring-gray-200 sm:h-[80px] sm:w-[80px]">
                    <UImage
                      src={creator.profileImage}
                      alt={creator.name}
                      width={80}
                      height={80}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <span className="absolute -right-[2px] -top-[4px] rounded-full bg-brand-1 px-[6px] py-[1px] text-[10px] font-bold leading-[1.4] text-white">
                    {creator.cookDiaryCount}+
                  </span>
                </div>
                <Text
                  variant="h6-medium-140"
                  className="w-full truncate text-center text-gray-800"
                >
                  {creator.name}
                </Text>
              </button>
            ))}
        <div className="h-[1px] w-[16px] flex-shrink-0" />
      </ScrollContainer>
      <SpaceY height={40} />
      <div className="grid grid-rows-2 gap-16px px-16px sm:grid-cols-2 sm:grid-rows-1">
        <RecipeMainBanner.Wrapper
          onClick={handleMoveToMyDiary}
          bgColor="bg-brand-16"
        >
          <RecipeMainBanner.Icon
            src={me?.hasRecipeCreatorProfile ? creatorIcon : bookmarkIcon}
            alt={me?.hasRecipeCreatorProfile ? '마이 다이어리' : '북마크'}
          />
          <RecipeMainBanner.TitleAndCaption
            title={me?.hasRecipeCreatorProfile ? '마이 다이어리' : '북마크'}
            caption="나의 채소기록"
          />
        </RecipeMainBanner.Wrapper>
        <RecipeMainBanner.Wrapper
          onClick={openRecipeModal}
          bgColor="bg-brown-400"
        >
          <RecipeMainBanner.Icon src={recipePaperIcon} alt={'레시피 페이퍼'} />
          <RecipeMainBanner.TitleAndCaption
            title={'레시피 페이퍼'}
            caption="이번 주 추천 요리"
          />
        </RecipeMainBanner.Wrapper>
      </div>
    </div>
  );
};

export default RecipeMainCertifiedCreators;
